import plans from "_shared/constants/plans";
import { useUserSubscriptionsQuery } from "_shared/graphql";
import { API } from "aws-amplify";
import { useState, useEffect } from "react";
import { useSubscriptionsContext } from "../contexts/SubscriptionsContext";
import { analytics } from "_shared/utils/analytics";

const useSubChangeDetails = () => {
  const {
    selectedAddons,
    confirmSubscriptionChange,
    userSubscriptions,
    planChange,
    currentPlanIndex,
    selectedPlanIndex,
  } = useSubscriptionsContext();

  const [addedSubscriptions, setAddedSubscriptions] = useState<any[]>();
  const [removedSubscriptions, setRemovedSubscriptions] = useState<any[]>();
  const [proration, setProration] = useState("");

  const { data: userSubscriptionData } = useUserSubscriptionsQuery();

  const monthlyTotal = addedSubscriptions
    ?.reduce((acc, sub) => acc + sub.monthly_daily_price, 0)
    .toFixed(2);

  useEffect(() => {
    const currentKeys = userSubscriptions
      .filter((sub) => !sub.subscribed?.expires)
      .map((sub) => sub.key!);
    const addedKeys = [...selectedAddons, plans[selectedPlanIndex].key].filter(
      (sub) => !currentKeys.includes(sub),
    );
    const addedSubs = userSubscriptionData?.subscriptions.filter((sub) =>
      addedKeys.includes(sub.id),
    );
    setAddedSubscriptions(addedSubs);
    const removedKeys = currentKeys.filter(
      (sub) =>
        !selectedAddons.includes(sub) && sub !== plans[selectedPlanIndex].key,
    );
    const removedSubs = userSubscriptionData?.subscriptions.filter((sub) =>
      removedKeys.includes(sub.key!),
    );
    setRemovedSubscriptions(removedSubs);
  }, [userSubscriptions, selectedAddons, selectedPlanIndex]);

  useEffect(() => {
    if (!addedSubscriptions) return setProration("");
    const getProration = async () => {
      const addedPriceIds = addedSubscriptions.map(
        (sub) => sub.monthly_daily_price_id,
      );
      const removedPriceIds = removedSubscriptions?.map(
        (sub) => sub.monthly_daily_price_id,
      );
      const prorationRes = await API.post(
        "auth",
        "/cognito/estimateSubscription",
        {
          body: {
            addedPriceIds,
            removedPriceIds,
          },
        },
      );
      if (prorationRes === "0.00") return setProration("");
      setProration(prorationRes);
    };
    getProration();
  }, [addedSubscriptions]);

  useEffect(() => {
    // Check included addons
    const plan = plans[selectedPlanIndex];
    setAddedSubscriptions((prev) =>
      prev?.filter(
        (sub) => !plan.includedAddons || !plan.includedAddons.includes(sub.key),
      ),
    );
  }, [selectedPlanIndex]);

  const handleConfirm = async () => {
    if (!addedSubscriptions || !removedSubscriptions) return;

    const analyticsData = {
      addedSubscriptions,
      removedSubscriptions,
    };
    if (planChange === "downgrade") {
      analytics.track("Subscription Downgraded", analyticsData);
    } else if (planChange === "cancel") {
      analytics.track("Subscription Cancelled", analyticsData);
    } else if (planChange === "upgrade") {
      analytics.track("Subscription Upgraded", analyticsData);
    } else {
      analytics.track("Subscription Changed", analyticsData);
    }

    const addedPriceIds = addedSubscriptions.map(
      (sub) => sub.monthly_daily_price_id,
    );
    const removedPriceIds = removedSubscriptions.map(
      (sub) => sub.monthly_daily_price_id,
    );

    await confirmSubscriptionChange(addedPriceIds, removedPriceIds);
  };

  return {
    addedSubscriptions,
    removedSubscriptions,
    proration,
    monthlyTotal,
    handleConfirm,
    hasChange:
      addedSubscriptions?.length! > 0 || removedSubscriptions?.length! > 0,
  };
};

export default useSubChangeDetails;
